import {
  MEDICATIONS_DOSE,
  MEDICATIONS_FREQUENCY,
} from '@constants/extension-codes';
import {
  PERIOD_UNIT_NOUNS,
  getFrequencyPreset,
} from '@constants/medication-dosage';

import { formatFhirDate, getFhirDate } from '@utils/fhir';
import {
  type DosageStep,
  describeDosage,
  describeFrequency,
  dosageToSteps,
  stepsToDosage,
  summarizeForList,
} from '@utils/fhir-mappers/dosageSteps';
import {
  type ContainedMedication,
  type MedicationIngredientRow,
  codeableConceptName,
  containedMedication,
  containedToIngredientRows,
  formatStrengths,
  ingredientRowsToContained,
} from '@utils/fhir-mappers/medicationIngredients';

import type {
  CodeableConcept,
  Dosage,
  MedicationModel,
  MedicationStatementModel,
  MedicationStatementModelInput,
} from 'generated/types';
import { StatementStatus } from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type { DosageStep } from '@utils/fhir-mappers/dosageSteps';

export type MedicationValues = Pick<
  MedicationStatementRecord,
  | 'acquisitionType'
  | 'endingDate'
  | 'instructions'
  | 'route'
  | 'selfReported'
  | 'startingDate'
  | 'status'
> & {
  title: string;
  associatedResources?: string[];
  isPatient: boolean;
  /** Ordered dose steps; >1 step = taper/titration. */
  dosageSteps: DosageStep[];
  /** One row per active ingredient; >1 row = combination product. */
  ingredients: MedicationIngredientRow[];
  /** Administration form of the product -> contained `Medication.form`. */
  doseForm: string;
  /**
   * Whether the "at least one of strength or dose" rule applies to this save.
   * True on a new record and on one that arrived with either half. False for a
   * record that was already missing both: the rule is there to stop a curator
   * leaving a new record empty, not to lock them out of a sparse legacy one
   * they only opened to fix its end date.
   */
  requireAmount: boolean;
};

export const MEDICATION_CATEGORY_MAP: Record<string, string> = {
  prescription: 'Prescription',
  overthecounter: 'Over the Counter',
};

export const MEDICATION_STATUS_MAP: Record<StatementStatus, string> = {
  [StatementStatus.active]: 'Active',
  [StatementStatus.intended]: 'Intended',
  [StatementStatus.unknown]: 'Unknown',
  [StatementStatus.completed]: 'Completed',
  [StatementStatus.enteredInError]: 'Entered In Error',
  [StatementStatus.stopped]: 'Stopped',
  [StatementStatus.onHold]: 'On Hold',
  [StatementStatus.notTaken]: 'Not Taken',
};
export type FormValues = BaseFhirFormValues & MedicationValues;

export class MedicationStatementRecord extends FhirResource {
  protected model: MedicationStatementModel;

  public constructor(model: MedicationStatementModel) {
    super(model);
    this.model = model;
  }

  public get recordType() {
    // esliore-dangle
    return this.model.__typename;
  }

  /**
   * Medication name, in the same order as the backend's `resolveMedicationName`:
   * the contained `Medication` is authoritative, `medicationReference.display` is
   * a denormalized copy of its `code.text`, and `medicationCodeableConcept` only
   * appears on records the medication[x] migration has not reached and on
   * responses the read shim has touched. Each CodeableConcept falls back to its
   * first coding display, since EMR-sourced concepts routinely arrive coded with
   * no `text`.
   *
   * Not identical to the backend, in two places. It trims `display` and treats an
   * empty string as absent. And when `medicationReference` names a contained id
   * that isn't there, `containedMedication` falls back to the only Medication
   * present rather than resolving nothing — see the note there for why.
   */
  public get medication() {
    return (
      codeableConceptName(this.containedMedication?.code) ??
      this.model.medicationReference?.display?.trim() ??
      codeableConceptName(this.model.medicationCodeableConcept)
    );
  }

  /**
   * One row per ingredient. Empty for a product whose strengths were never
   * captured — a single-ingredient record is not required to carry them.
   */
  public get ingredients(): MedicationIngredientRow[] {
    return containedToIngredientRows(
      this.model.contained as ContainedMedication[] | null | undefined,
      this.model.medicationReference?.reference
    );
  }

  public set ingredients(_value: MedicationIngredientRow[]) {
    // No-op: the product is written wholesale by getModelInput via
    // setMedicationProduct. Kept so getModelInput's blind value-assignment loop
    // doesn't throw on this key.
  }

  /**
   * Administration form of the product ("tablet", "capsule"), from the contained
   * `Medication.form`. Nothing in the UI edits it; it round-trips, and OCR can
   * seed it on a new record.
   */
  public get doseForm(): string {
    return this.containedMedication?.form?.text ?? '';
  }

  public set doseForm(_value: string) {
    // No-op: written by getModelInput via setMedicationProduct.
  }

  /** Canonical combination strength for display, e.g. `875/125 mg`. */
  public get strength(): string | undefined {
    return formatStrengths(this.ingredients) || undefined;
  }

  public get acquisitionType() {
    return this.model.category?.coding?.find(
      ({ system }) => system === MedicationStatementRecord.VITALL_SYSTEM
    )?.code;
  }

  public set acquisitionType(value: string | null | undefined) {
    const category: CodeableConcept | null = value
      ? {
          coding: [
            {
              code: value,
              display: MEDICATION_CATEGORY_MAP[value],
              system: MedicationStatementRecord.VITALL_SYSTEM,
            },
          ],
          text: MEDICATION_CATEGORY_MAP[value],
        }
      : null;
    this.model = {
      ...this.model,
      category,
    };
  }

  // Prescription, Over-the-counter
  public get acquisitionTypeName() {
    return this.model.category?.text;
  }

  public get status() {
    return this.model.status;
  }

  public set status(value: string) {
    this.model = {
      ...this.model,
      status: value as StatementStatus,
    };
  }

  public get route() {
    return this.model.dosage?.[0]?.route?.text ?? undefined;
  }

  /** Form representation of the (possibly multi-step) dosage. */
  public get dosageSteps(): DosageStep[] {
    return dosageToSteps(this.model.dosage ?? undefined);
  }

  public set route(value: string | null | undefined) {
    // Non-destructive: only mutate dosage[0].route, preserving any additional
    // dosage elements (tapers/titrations) and structured fields (doseRange,
    // boundsDuration, periodMax, dayOfWeek, ...). Skip when unchanged so that
    // re-saving an untouched multi-element entry can't flatten it.
    if ((value ?? undefined) === (this.route ?? undefined)) return;
    const dosage = this.cloneDosage();
    if (value) dosage[0].route = { text: value };
    else delete dosage[0].route;
    this.model = { ...this.model, dosage };
  }

  public get timing(): string[] {
    const when = this.model.dosage?.[0]?.timing?.repeat?.when;
    return Array.isArray(when)
      ? when.filter((w): w is string => Boolean(w))
      : [];
  }

  public set timing(values: string[]) {
    const dosage: Dosage[] = [...(this.model.dosage ?? [{}])];
    dosage[0] = {
      ...dosage[0],
      timing: {
        ...dosage[0].timing,
        repeat: {
          ...dosage[0].timing?.repeat,
          when: values.length > 0 ? values : undefined,
        },
      },
    };
    this.model = { ...this.model, dosage };
  }

  /**
   * Only `patientInstruction`. It used to append `additionalInstruction` displays
   * too, but the form writes this field straight back to `patientInstruction`
   * while the merge preserves `additionalInstruction`, so every save appended
   * another copy and the field grew without bound. `additionalInstruction` is
   * coded content the form can't edit; it round-trips untouched instead.
   */
  public get instructions() {
    return this.model.dosage?.[0]?.patientInstruction ?? '';
  }

  public set instructions(value: string | null | undefined) {
    // Non-destructive: patch dosage[0].patientInstruction in place, preserving
    // additional dosage elements and structured fields. Skip when unchanged.
    if ((value ?? undefined) === (this.instructions ?? undefined)) return;
    const dosage = this.cloneDosage();
    if (value) dosage[0].patientInstruction = value;
    else delete dosage[0].patientInstruction;
    this.model = { ...this.model, dosage };
  }

  public get startingDate() {
    const { effectiveDateTime, effectivePeriod } = this.model;
    return effectiveDateTime ?? effectivePeriod?.start;
  }

  public set startingDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      effectiveDateTime: value,
      effectivePeriod: {
        start: value,
        end: this.endingDate,
      },
    };
  }

  public get endingDate() {
    return this.model.effectivePeriod?.end;
  }

  public set endingDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      effectivePeriod: {
        end: value,
      },
    };
  }

  public get doseValue(): number | undefined {
    return (
      this.model.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity?.value ?? undefined
    );
  }

  public get doseUnit(): string | undefined {
    return (
      this.model.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity?.unit ?? undefined
    );
  }

  /** Low/high bounds when the dose is expressed as a range (e.g. "1 to 2 tablets"). */
  public get doseRange():
    { low?: number; high?: number; unit?: string } | undefined {
    const range = this.model.dosage?.[0]?.doseAndRate?.[0]?.doseRange;
    if (!range) return undefined;
    return {
      low: range.low?.value ?? undefined,
      high: range.high?.value ?? undefined,
      unit: range.high?.unit ?? range.low?.unit ?? undefined,
    };
  }

  /** Combined dose string for display. Falls back to extension if structured dosage is empty. */
  public get dose(): string | undefined {
    if (this.doseValue !== undefined && this.doseUnit) {
      return `${this.doseValue} ${this.doseUnit}`;
    }
    const range = this.doseRange;
    if (range && (range.low !== undefined || range.high !== undefined)) {
      const unit = range.unit ? ` ${range.unit}` : '';
      if (range.low !== undefined && range.high !== undefined) {
        return `${range.low} to ${range.high}${unit}`;
      }
      return `${range.low ?? range.high}${unit}`;
    }
    const extension = this.findExtension(MEDICATIONS_DOSE);
    return extension?.valueString ?? undefined;
  }

  /**
   * One line for the medications list and the share view, under the drug name:
   * the product's strength, then what the structured dosage says. See
   * `getDosageSummary` for how it is built and why.
   */
  public get dosageSummary(): string {
    return this.getDosageSummary();
  }

  public set dose(_value: string | null | undefined) {
    // No-op: the structured dosage is authoritative and is written wholesale by
    // getModelInput via stepsToDosage. Kept so getModelInput's blind
    // value-assignment loop doesn't throw on this key.
  }

  public get frequencyPreset(): string | undefined {
    const repeat = this.model.dosage?.[0]?.timing?.repeat;
    if (!repeat) return undefined;
    return (
      getFrequencyPreset({
        // Under PRN the per-period count is stored as frequencyMax.
        frequency: repeat.frequencyMax ?? repeat.frequency,
        period: repeat.period,
        periodMax: repeat.periodMax,
        periodUnit: repeat.periodUnit,
        count: repeat.count,
      }) || undefined
    );
  }

  /** True when the medication is taken PRN, additively to any schedule. */
  public get asNeeded(): boolean {
    return Boolean(this.model.dosage?.[0]?.asNeededBoolean);
  }

  public get frequencyTimes(): number | undefined {
    const repeat = this.model.dosage?.[0]?.timing?.repeat;
    return repeat?.frequencyMax ?? repeat?.frequency ?? undefined;
  }

  public get frequencyEvery(): number | undefined {
    return this.model.dosage?.[0]?.timing?.repeat?.period ?? undefined;
  }

  public get frequencyUnit(): string | undefined {
    return this.model.dosage?.[0]?.timing?.repeat?.periodUnit ?? undefined;
  }

  /** Course duration (FHIR Timing.repeat.boundsDuration), e.g. "for 30 days". */
  public get duration(): string | undefined {
    const bd = this.model.dosage?.[0]?.timing?.repeat?.boundsDuration;
    const value = bd?.value;
    if (value === null || value === undefined) return undefined;
    const unitMap: Record<string, string> = {
      d: 'day',
      wk: 'week',
      mo: 'month',
      h: 'hour',
      min: 'minute',
      a: 'year',
    };
    const unit = bd?.unit ?? undefined;
    const base = (unit ? unitMap[unit] : undefined) ?? unit ?? '';
    const label = base && value !== 1 ? `${base}s` : base;
    return label ? `${value} ${label}` : `${value}`;
  }

  /** Human-readable frequency string. Falls back to extension. */
  public get frequency(): string | undefined {
    const described = describeFrequency(this.dosageSteps[0]);
    if (described) return described;
    const extension = this.findExtension(MEDICATIONS_FREQUENCY);
    return extension?.valueString ?? undefined;
  }

  public set frequency(_value: string | null | undefined) {
    // No-op: the structured dosage is authoritative and is written wholesale by
    // getModelInput via stepsToDosage. Kept so getModelInput's blind
    // value-assignment loop doesn't throw on this key.
  }

  /** Optional ceiling, e.g. "8 tablets per day". */
  public get maxDosePerPeriod(): string | undefined {
    const step = this.dosageSteps[0];
    if (!step.maxDoseValue || !step.maxDoseUnit || !step.maxDosePeriodUnit) {
      return undefined;
    }
    const noun =
      PERIOD_UNIT_NOUNS[step.maxDosePeriodUnit] ?? step.maxDosePeriodUnit;
    const per = Number(step.maxDosePeriodValue) || 1;
    const period = per === 1 ? noun : `${per} ${noun}s`;
    return `${step.maxDoseValue} ${step.maxDoseUnit} per ${period}`;
  }

  /**
   * The contained `Medication` this record's `medicationReference` points at.
   *
   * Cast rather than taken from the schema for the same reason the backend keeps
   * `contained` as `Resource[]`: the FHIR server can return any resource type in
   * there, so the resourceType check inside `containedMedication` is a real
   * runtime filter and not a formality.
   */
  private get containedMedication() {
    return containedMedication(
      this.model.contained as ContainedMedication[] | null | undefined,
      this.model.medicationReference?.reference
    );
  }

  public getDateStart() {
    const { effectiveDateTime, effectivePeriod } = this.model;
    if (effectiveDateTime) {
      return getFhirDate(effectiveDateTime);
    }
    return getFhirDate(effectivePeriod?.start);
  }

  public getDateEnd() {
    const { effectivePeriod } = this.model;
    return getFhirDate(effectivePeriod?.end);
  }

  /**
   * Backs `dosageSummary`. Built from `describeDosage`, the same summarizer the
   * drilldown's read view and the save preview use, so no two surfaces can word
   * a record differently — the list's own version printed "1 to 2 tablet"
   * against the drilldown's "1 to 2 tablets".
   *
   * The legacy `medications/dose` extension is only reached when there is
   * nothing structured to say. Nothing has written that extension since the
   * dosage migration, and once VEG-760 split strength from quantity a record
   * with a strength and no dose became ordinary — so `dose`'s fallback started
   * serving whatever string had been sitting on the resource for years, which
   * on our test data reads "dosage".
   *
   * `route: false` is for the PDF export, whose table prints the route in a
   * column of its own. Dropping it here rather than trimming the finished
   * string keeps that surface on the same code path, and takes the route out of
   * the fallbacks too — leaving it there would hand back the very word the
   * caller asked to omit.
   */
  public getDosageSummary({ route: withRoute = true } = {}): string {
    const dosage = this.model.dosage?.[0] ?? undefined;
    // Without the prose: dosing instructions run to a sentence and would crowd
    // out the dose the card exists to show.
    // A route on its own is not a dose. Without this a legacy record carrying
    // "Oral" and a `medications/dose` extension printed "Oral" on the card and
    // hid the only dose it had.
    const route = dosage?.route?.text?.trim();
    const said = (line: string) => (line && line !== route ? line : '');

    const summary = said(
      summarizeForList(dosage, this.strength, { route: withRoute })
    );
    if (summary) return summary;

    // Nothing structured to show. A brand-name combination product keeps its
    // whole dose in `Dosage.text`, and an unmigrated record keeps it in the
    // legacy extension, so both are worth more here than an empty line. The
    // route is last: better than blank, worse than an actual dose.
    return (
      said(describeDosage(dosage, { route: withRoute })) ||
      this.findExtension(MEDICATIONS_DOSE)?.valueString ||
      (withRoute ? route : '') ||
      ''
    );
  }

  public getDateStartStr() {
    const { effectiveDateTime, effectivePeriod } = this.model;
    if (effectiveDateTime) {
      return formatFhirDate(effectiveDateTime, { showTime: false });
    }
    return formatFhirDate(effectivePeriod?.start, { showTime: false });
  }

  public getDateEndStr() {
    const { effectivePeriod } = this.model;
    return formatFhirDate(effectivePeriod?.end, { showTime: false });
  }

  public isActive() {
    const { status } = this.model;
    return status === 'active';
  }

  public getStatusCategory() {
    const { status } = this.model;
    switch (status) {
      case StatementStatus.active:
      case StatementStatus.intended:
        return 'active';
      case StatementStatus.unknown:
        return 'unknown';
      case StatementStatus.enteredInError:
      case StatementStatus.stopped:
      case StatementStatus.onHold:
      case StatementStatus.notTaken:
      case StatementStatus.completed:
        return 'inactive';
      default:
        return 'inactive';
    }
  }

  public getModelInput(
    values?: MedicationValues
  ): MedicationStatementModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.setMedicationProduct(values);

      // Build the (possibly multi-step) structured dosage from the form steps.
      // This is authoritative: route is applied to every element and
      // instructions to the first, so the dose-step list round-trips losslessly
      // (ranges, tapers with sequence, durations, periodMax, when, etc.).
      this.model = {
        ...this.model,
        // Input/output dosage shapes are structurally compatible here.
        dosage: stepsToDosage(values.dosageSteps, {
          route: values.route ?? undefined,
          instructions: values.instructions ?? undefined,
          // A medication update is a full PUT, so the stored dosage has to be
          // merged onto or fields the form can't express are dropped.
          existing: this.model.dosage ?? undefined,
        }) as unknown as Dosage[],
      };
    }
    // Can't have both effectivePeriod and effectiveDateTime
    if (this.model.effectivePeriod?.start && this.model.effectivePeriod.end) {
      delete this.model['effectiveDateTime'];
    } else {
      this.model.effectiveDateTime =
        this.model.effectiveDateTime ?? this.model.effectivePeriod?.start;
      delete this.model['effectivePeriod'];
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return {
      ...cleaned,
      status: cleaned.status as StatementStatus,
    };
  }

  /**
   * Write the product into the reference model: a contained `Medication` holding
   * the name and each ingredient's strength, plus the `medicationReference` that
   * points at it.
   *
   * `medicationCodeableConcept` is always dropped. `medication[x]` is a FHIR
   * choice type, so a resource carrying both members is a validation error
   * HealthLake rejects — and on a legacy record the concept is exactly what this
   * write replaces.
   *
   * The form owns the product name and the ingredient rows; everything else the
   * stored `Medication` carries is merged through, as is every other contained
   * resource. Only the Medication the reference resolves to is rewritten.
   */
  private setMedicationProduct(values: MedicationValues) {
    const existing = this.containedMedication;

    /**
     * Converting a legacy record has to carry its codeable concept into the
     * contained `Medication.code`, or an EMR-sourced RxNorm code is lost the
     * first time a curator saves. VEG-725's read shim used to synthesize a
     * contained Medication from the concept, so this arrived pre-merged; it stops
     * doing that (engage-v2-backend 6a90c4b4), which makes the carry-over ours.
     */
    const seed =
      existing ??
      (this.model.medicationCodeableConcept
        ? { code: this.model.medicationCodeableConcept }
        : undefined);

    const contained = ingredientRowsToContained(
      values.title,
      values.ingredients,
      values.doseForm,
      seed
    );
    // Nothing to write means the form has no product name and no ingredient, so
    // leave whatever the record already carries alone. Dropping the concept here
    // would wipe the name off a legacy record, and dropping the reference would
    // trip the proxy's downgrade guard on the next save.
    if (!contained) return;

    const model = { ...this.model };
    delete model.medicationCodeableConcept;

    const others = (model.contained ?? []).filter((resource) => {
      return (resource as ContainedMedication) !== existing;
    });
    this.model = {
      ...model,
      contained: [...others, contained] as MedicationModel[],
      medicationReference: {
        ...model.medicationReference,
        reference: `#${contained.id}`,
        display: values.title.trim() || null,
      },
    };
  }

  /**
   * Shallow-clone the dosage array (and element 0) so mutations stay immutable
   * while preserving every other element and field by reference.
   */
  private cloneDosage(): Dosage[] {
    const dosage: Dosage[] = this.model.dosage?.length
      ? [...this.model.dosage]
      : [{}];
    dosage[0] = { ...dosage[0] };
    return dosage;
  }
}
